import { QueryClient } from '@tanstack/react-query';

import { env } from '@/env';

import { supabase } from './supabase';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
    },
  },
});

interface ApiFetchOptions extends RequestInit {
  readonly path: string;
}

// fetcher que añade Authorization: Bearer <access_token> de la sesión
// Supabase actual y normaliza errors a una shape predecible. Las features
// pueden envolver este helper en queryFn/mutationFn de TanStack Query.
//
// Si la sesión cacheada está vencida (`expires_at < now`), forzamos un
// refresh antes de mandar el header — `getSession()` no refresca por sí
// solo, y un token stale le pega al backend con 401 silencioso.
async function getValidAccessToken(): Promise<string | undefined> {
  const { data } = await supabase.auth.getSession();
  const session = data.session;
  if (!session) return undefined;

  const nowSec = Math.floor(Date.now() / 1000);
  const expiresAt = session.expires_at ?? 0;
  if (expiresAt > nowSec + 5) return session.access_token;

  const { data: refreshed } = await supabase.auth.refreshSession();
  return refreshed.session?.access_token ?? session.access_token;
}

// Variante de apiFetch para descarga de archivos binarios (Excel, PDF).
// Reusa el mismo auth + CORS + error handling, pero devuelve el blob
// directo en vez de parsear JSON. Hace falta porque apiFetch hace
// res.json() y un .xlsx no es JSON — sin esto la descarga del export
// devolvía HTML (el index.html del admin por el rewrite default del
// browser cuando navega a la URL sin token).
export async function apiFetchBlob({ path, ...init }: ApiFetchOptions): Promise<Blob> {
  const accessToken = await getValidAccessToken();
  const headers = new Headers(init.headers);
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  const url = `${env.VITE_API_URL}${path}`;
  const res = await fetch(url, { credentials: 'include', ...init, headers });
  if (!res.ok) {
    const body = await res.text();
    throw new ApiError({ status: res.status, body, url });
  }
  return res.blob();
}

export async function apiFetch<T>({ path, ...init }: ApiFetchOptions): Promise<T> {
  const accessToken = await getValidAccessToken();

  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  } else if (path.startsWith('/v1/')) {
    // El backend rechazará con 401. Log para hacer evidente el caso en dev
    // (en prod la auth-guard del router debería evitar llegar acá).
    console.warn('[apiFetch] No Supabase session — request will hit %s without token', path);
  }

  const url = `${env.VITE_API_URL}${path}`;
  // credentials: 'include' es necesario para que el browser envíe la
  // cookie HttpOnly `__Host-trust-device` (sprint MFA trust device) en
  // requests cross-origin (admin ↔ api.simplificaloapp.cl). El backend ya
  // tiene credentials: true en su CORS config.
  const res = await fetch(url, { credentials: 'include', ...init, headers });

  if (!res.ok) {
    const body = await res.text();
    throw new ApiError({ status: res.status, body, url });
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return (await res.json()) as T;
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly body: string;
  public readonly url: string;

  constructor(args: { status: number; body: string; url: string }) {
    super(`API ${args.status} on ${args.url}`);
    this.name = 'ApiError';
    this.status = args.status;
    this.body = args.body;
    this.url = args.url;
  }

  /**
   * Mensaje amigable parseado del body de error del backend. Convención
   * shared: `{ error: { code, message, ... } }`. Si no puede parsear (body
   * vacío, no-JSON, shape distinto), cae a un mensaje basado en status.
   * Usado por los toasts para no mostrar "API 400 on /v1/..." genérico.
   */
  get friendlyMessage(): string {
    if (this.body) {
      try {
        const parsed = JSON.parse(this.body) as {
          error?: {
            message?: string;
            code?: string;
            details?: { fieldErrors?: Record<string, string[]> };
          };
          message?: string;
        };
        // Zod VALIDATION_ERROR — mostrar campo + mensaje específico.
        if (
          parsed.error?.code === 'VALIDATION_ERROR' &&
          parsed.error.details?.fieldErrors
        ) {
          const entries = Object.entries(parsed.error.details.fieldErrors);
          if (entries.length > 0) {
            return entries
              .map(([field, msgs]) => `${field}: ${msgs.join(', ')}`)
              .join(' · ');
          }
        }
        if (parsed.error?.message) return parsed.error.message;
        if (parsed.message) return parsed.message;
      } catch {
        // body no es JSON — caer al fallback
      }
    }
    if (this.status === 401) return 'Sesión expirada. Inicia sesión de nuevo.';
    if (this.status === 403) return 'No tienes permiso para esta acción.';
    if (this.status === 404) return 'Recurso no encontrado.';
    if (this.status === 422) return 'Datos inválidos.';
    if (this.status >= 500) return 'Error en el servidor. Inténtalo de nuevo en unos segundos.';
    return `Error ${this.status}`;
  }

  /** Código de error del backend si lo emitió (ej. 'MOTIVO_PERDIDA_REQUIRED'). */
  get errorCode(): string | null {
    if (!this.body) return null;
    try {
      const parsed = JSON.parse(this.body) as { error?: { code?: string } };
      return parsed.error?.code ?? null;
    } catch {
      return null;
    }
  }
}
